// SaveBar — bottom toolbar of the editor pane.
// Contains: dirty indicator, optional note input, Save button.
// Wires Cmd/Ctrl+S to trigger save with preventDefault.

import { useState, useEffect, useRef } from "react";
import { History, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface SaveBarProps {
  isDirty: boolean;
  isSaving: boolean;
  /** True when the frontmatter has validation errors — disables Save. */
  hasValidationErrors: boolean;
  onSave: (note: string) => void;
  /** Opens the version-history drawer. When omitted, the History button is hidden. */
  onOpenHistory?: () => void;
}

export function SaveBar({
  isDirty,
  isSaving,
  hasValidationErrors,
  onSave,
  onOpenHistory,
}: SaveBarProps) {
  const [noteVisible, setNoteVisible] = useState(false);
  const [note, setNote] = useState("");
  const noteRef = useRef<HTMLInputElement>(null);

  const canSave = isDirty && !hasValidationErrors && !isSaving;

  function handleSave() {
    if (!canSave) return;
    onSave(note);
  }

  function handleSaveSuccess() {
    // Called by parent after successful save to clear the note.
    setNote("");
    setNoteVisible(false);
  }

  // Expose a reset handle via a ref-like pattern by watching isSaving go false.
  // When isSaving transitions from true → false and isDirty is now false, clear the note.
  const wasSavingRef = useRef(false);
  useEffect(() => {
    if (wasSavingRef.current && !isSaving && !isDirty) {
      handleSaveSuccess();
    }
    wasSavingRef.current = isSaving;
  }, [isSaving, isDirty]);

  // Cmd/Ctrl+S handler — must preventDefault to block the browser's native save dialog.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // handleSave is intentionally omitted — we access it via closure and refresh the listener
    // only when the dependencies that affect canSave change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, hasValidationErrors, isSaving, note]);

  function toggleNote() {
    setNoteVisible((v) => {
      if (!v) {
        // Focus the input after it mounts.
        setTimeout(() => noteRef.current?.focus(), 0);
      }
      return !v;
    });
  }

  return (
    <div className="h-12 border-t border-border px-4 flex items-center justify-between gap-3">
      {/* Left: dirty indicator */}
      <div className="flex items-center gap-2 text-sm">
        {isDirty && (
          <span className="text-warning" aria-label="Unsaved changes" title="Unsaved changes">
            ●
          </span>
        )}
        <span className="text-xs text-muted-foreground">{isDirty ? "Unsaved changes" : ""}</span>
      </div>

      {/* Right: history + note toggle + save button */}
      <div className="flex items-center gap-2">
        {onOpenHistory && (
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={onOpenHistory}
            className="text-xs"
            aria-label="Version history"
          >
            <History className="w-3.5 h-3.5 flex-shrink-0" />
            History
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={toggleNote}
          className="text-xs text-muted-foreground"
          aria-label="Toggle note input"
        >
          {noteVisible ? "− note" : "+ note"}
        </Button>

        {noteVisible && (
          <Input
            ref={noteRef}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            size={1} // visual size; actual width via className
            className="h-7 w-48 text-xs"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "s") {
                e.preventDefault();
                handleSave();
              }
            }}
          />
        )}

        <Button
          size="sm"
          disabled={!canSave}
          onClick={handleSave}
          type="button"
          aria-label="Save file"
        >
          {isSaving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
          ) : (
            <Save className="w-3.5 h-3.5 flex-shrink-0" />
          )}
          Save
        </Button>
      </div>
    </div>
  );
}
